import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Tabs,
  Tab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  DialogActions,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { styled } from "@mui/system";
import IconButton from "@mui/material/IconButton";
import ImpersonateIcon from "@mui/icons-material/PeopleAlt";
import CreateUserModal from "../CreateUser/CreateUserModal";
import { createUser } from "../../features/createUser/createUserAction";
import { useDispatch, useSelector } from "react-redux";
import { getLicenseDetailAction } from "../../features/licenseDetail/licenseDetailAction";
import moment from "moment";
import EditIcon from "@mui/icons-material/Edit";
import CreateLicenseModal from "../CreateLicense/CreateLicenseModal";
import { editLicenseAction } from "../../features/editLicense/editLicenseAction";
import { getUserDetailAction } from "../../features/userDetail/userDetailAction";
import UserDetail from "../UserDetail/UserDetail";
import { makeStyles } from "@mui/styles";
import { getImpersonateDetailAction } from "../../features/impersonateDetail/impersonateDetailAction";
import { toast } from "react-toastify";
import Features from "../Features/Index";
import { getFeaturesListByTenant } from "../../features/featuresDetails/featureDetailsAction";

const CommonTypography = styled(Typography)({
  display: "flex",
  flexDirection: "column",
  gap: 5,
  width: "49%",
});

const useStyles = makeStyles({
  tabAlign: {
    cursor: "pointer !important",
    fontWeight: "600 !important",
    textTransform: "capitalize !important",
    fontSize: "18px !important",
  },
  tenantDetailBoxLeft: {
    justifyContent: "space-between",
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    rowGap: "20px",
  },
  confirmButton: {
    color: "inherit !important",
    textTransform: "capitalize !important",
    fontWeight: "600 !important",
    borderRadius: "4px !important",
    cursor: "pointer !important",
  },
  createUserBtn: {
    textTransform: "capitalize !important",
    fontSize: "18px !important",
    fontWeight: "600 !important",
  },
  impersonateBox: {
    fontWeight: "600 !important",
    borderBottom: "1px solid lightgrey",
  },
  boxSpacing: {
    paddingTop: "20px !important",
  },
  impersonateContent: {
    display: "flex",
    gap: "4px",
  },
  impersonateTenant: {
    color: "#1976d2 !important",
    display: "contents !important",
  },
  cancelButton: {
    color: "inherit !important",
    textTransform: "capitalize !important",
    fontWeight: "600 !important",
    border: "1px solid",
    borderRadius: "4px !important",
    padding: "6px 16px !important",
    cursor: "pointer",
  },
});

const TenantDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const classes = useStyles();
  
  // Get tenant from location state or localStorage as fallback
  const getTenant = () => {
    if (location.state?.tenant) {
      // Store in localStorage for future use
      localStorage.setItem('currentTenant', JSON.stringify(location.state.tenant));
      return location.state.tenant;
    }
    // Try to get from localStorage if no state
    const storedTenant = localStorage.getItem('currentTenant');
    return storedTenant ? JSON.parse(storedTenant) : null;
  };
  
  const [tenant, setTenant] = useState(getTenant);
  
  // Get tab from URL params or default to 0
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'users') return 1;
    if (tabParam === 'features') return 2;
    return 0; // default to tenant tab
  };
  
  const [tabValue, setTabValue] = useState(getInitialTab);
  
  // Update tab when URL changes
  useEffect(() => {
    setTabValue(getInitialTab());
  }, [searchParams]);
  const [openModal, setOpenModal] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [licenseData, setLicenseData] = useState("");
  const [userData, setUserData] = useState('')
  const [userName, setUserName] = useState('')
  const [licenseUrls, setLicenseUrls] = useState("");
  const [userRowData, setUserRowData] = useState([]);
  const [openImpersonatePopup, setOpenImpersonatePopup] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState("User");
  const [isAdmin, setIsAdmin] = useState(false);
  const [impersonateUserId, setImpersonateUserId] = useState(null);

  const dispatch = useDispatch();
  const userDetailData = useSelector((state) => state.userDetail.data);
  const needsRefresh = useSelector((state) => state.userDetail.needsRefresh);
  
  // Auto-refresh when needsRefresh flag is set
  useEffect(() => {
    if (needsRefresh && tenant?.id) {
      dispatch(getUserDetailAction({ tenantId: tenant.id }));
    }
  }, [needsRefresh, tenant?.id, dispatch]);
  
  const getLicenseDetail = async (id) => {
    dispatch(getLicenseDetailAction({ licenseId: id })).then((res) => {
      if (res?.payload) {
        setLicenseData(res?.payload?.data);
        setLicenseUrls(res?.payload?.urls);
      }
    });
  };

  const getUserDetail = async (id) => {
    dispatch(getUserDetailAction({ tenantId: id })).then((res) => {
      if (res?.payload) {
        setUserData(res?.payload?.data);
      }
    });
  };

  const getFeaturesListByTenantId = (id) => {
    dispatch(getFeaturesListByTenant({ id }));
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Update URL parameter based on tab
    const tabNames = ['tenant', 'users', 'features'];
    setSearchParams({ tab: tabNames[newValue] });
  };

  useEffect(() => {
    if (tenant?.id) {
      getLicenseDetail(tenant.id);
      getUserDetail(tenant.id);
    }
  }, [tenant]);

  useEffect(() => {
    if (licenseData?.id) {
      getFeaturesListByTenantId(licenseData?.id);
    }
  }, [licenseData?.id]);

  const handleImpersonate = (row) => {
    setUserName(row?.firstName);
    setImpersonateUserId(row?.id);
    setOpenImpersonatePopup(true);
    setIsAdmin(row?.isAdmin);
  }

  const handleImpersonateConfirm = () => {
    let impersonateData = {
      userId: impersonateUserId,
      tenantId: tenant?.id,
    };
    dispatch(getImpersonateDetailAction(impersonateData)).then((res) => {
      if (res?.payload && res?.payload?.success === "success") {
        const { token, user, impersonatorUrl, impersonating } = res.payload;
        if (impersonatorUrl) {
          const data = {
            token: token,
            currentUser: user,
            impersonating,
          };
          function encodeData(obj) {
            const jsonString = JSON.stringify(obj);
            return btoa(jsonString);
          }
          const encodedData = encodeData(data);
          const updatedUrl = `${impersonatorUrl}login?token=${token}`;
          window.location.href = updatedUrl;
        } else {
          console.error("Impersonator URL not available");
        }
      } else {
        console.error("Impersonation failed:", res.payload || res.error);
      }
    });
    setOpenImpersonatePopup(false);
  };

  useEffect(() => {
    setUserRowData(
      userDetailData?.userList?.map((item, index) => ({
        id: item?.uuid || index,
        firstName: item?.firstName,
        lastName: item?.lastName,
        email: item?.email,
        role: item?.role,
      }))
    );
  }, [userDetailData]);

  const userDataColumn = [
    {
      field: "firstName",
      headerName: "First Name",
      flex: 1,
    },
    {
      field: "lastName",
      headerName: "Last  Name",
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.6,
      sortable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Impersonate" arrow>
          <IconButton
            sx={{color: '#1976d2'}}
            onClick={() => handleImpersonate(params.row)}
          >
            <ImpersonateIcon />
          </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (!tenant) {
    return <Typography variant="h6">No tenant details available.</Typography>;
  }

  const handleCreateUser = () => {
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleUserCreate = async () => {
    if (firstName.trim() && email) {
      const newUserData = {
        domain: tenant.id,
        firstName: firstName,
        lastName: lastName,
        email,
        role: "admin",
        verified: 1,
      };
      
      try {
        const res = await dispatch(createUser(newUserData));
        if (res.type === 'createUser/fulfilled') {
          toast.success(res.payload?.message || 'User created successfully!');
          setTimeout(() => {
            dispatch(getUserDetailAction({ tenantId: tenant.id }));
          }, 500);
        }
      } catch (error) {
        console.error('Error creating user:', error);
      }
      
      setOpenModal(false);
      setLastName("");
      setFirstName("");
      setEmail("");
    }
  };

  return (
    <>
      <Box sx={{ padding: "20px", marginTop: 5 }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            marginBottom: 4,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab className={classes.tabAlign} label="Tenant" />
            <Tab className={classes.tabAlign} label="Users" />
            <Tab className={classes.tabAlign} label="Features" />
          </Tabs>
          <Box
            display="flex"
            flexDirection="row"
            gap={2}
            justifyContent="flex-start"
            alignItems="center"
            sx={{ height: "100%" }}
          >
            <Button
              onClick={handleCreateUser}
              variant="contained"
              color="primary"
              fullWidth
              className={classes.createUserBtn}
            >
              Create User
            </Button>
          </Box>
        </Box>

        {tabValue === 0 && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6} lg={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6" fontWeight={600}>
                    Tenant Details
                  </Typography>
                </CardContent>

                <Box sx={{ display: "flex" }}>
                  <CardContent className={classes.tenantDetailBoxLeft}>
                    <CommonTypography>
                      <strong>Name:</strong> {tenant.tenant}
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Domain:</strong> {tenant?.id}
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Created On:</strong>
                      <span>{tenant.createdOn}</span>
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Current Status:</strong>{" "}
                      <span
                        style={{
                          color: tenant.status === "Active" ? "green" : "red",
                        }}
                      >
                        {tenant.status}
                      </span>
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Primary Status:</strong>{" "}
                      <span
                        style={{
                          color: tenant.primary === "Active" ? "green" : "red",
                        }}
                      >
                        {tenant.primary}
                      </span>
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Expiry Date:</strong>
                      <span>{tenant.expiration}</span>
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Url:</strong>{" "}
                      <a
                        href={licenseData?.url}
                        style={{
                          textDecoration: "none",
                          wordBreak: "break-all",
                        }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {licenseData?.url}
                      </a>
                    </CommonTypography>
                  </CardContent>
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}

        {tabValue === 1 && (
          <Typography variant="h6" sx={{ textAlign: "center", marginTop: 4 }}>
            <UserDetail
              columns={userDataColumn}
              rows={userRowData}
              getRowId={(row) => row.email}
            />
          </Typography>
        )}
        {tabValue === 2 && (
          <Features
            hideFeatureButton
            hideEditIcon
            hideDeleteIcon
            hideAddIcon
            showToggle
            tenantId={tenant?.id}
          />
        )}

        <CreateUserModal
          openModal={openModal}
          handleCloseModal={handleCloseModal}
          handleUserCreate={handleUserCreate}
          lastName={lastName}
          setFirstName={setFirstName}
          firstName={firstName}
          setLastName={setLastName}
          email={email}
          setEmail={setEmail}
        />

        <Dialog
          open={openImpersonatePopup}
          onClose={() => setOpenImpersonatePopup(false)}
          sx={{
            "& .MuiDialog-paper": {
              width: "650px",
              maxWidth: "100%",
            },
          }}
        >
          <DialogTitle className={classes.impersonateBox}>
            Impersonate
          </DialogTitle>
          <DialogContent className={classes.boxSpacing}>
            <Typography className={classes.impersonateContent}>
              Do you want to impersonate as{" "}
              <InputLabel className={classes.impersonateTenant}>
                {userName}
              </InputLabel>
              {" "}?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ borderTop: "1px solid lightgrey" }}>
            <Button onClick={() => setOpenImpersonatePopup(false)}>
              <InputLabel className={classes.cancelButton}>Cancel</InputLabel>
            </Button>
            <Button
              onClick={handleImpersonateConfirm}
              variant="contained"
              color="primary"
              disabled={!selectedPortal}
              sx={{ marginRight: "0px" }}
            >
              <InputLabel className={classes.confirmButton}>Confirm</InputLabel>
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default TenantDetail;
